import { DEFAULT_RECEIPT_LAYOUT, resolveReceiptLocale, type ReceiptLayout } from "@astroplanet/shared";
import { api } from "./api";
import { formatDateTime, localName, money } from "./format";
import { emitScan } from "./scanBus";
import type { Locale, Receipt } from "./types";

/** Printable receipt payload (also consumed by the Electron ESC/POS printer in Phase 5). */
export interface ReceiptPrintData {
  locale: Locale;
  stationName: string;
  /** Address / phone / tax-number lines under the station name. */
  headerLines?: string[];
  /** Custom thank-you line (replaces the default). */
  footer?: string | null;
  receiptNo: string;
  createdAt: string;
  terminal: string | null;
  items: Array<{ description: string; descriptionAr: string | null; qty: number; unitPrice: number; lineTotal: number }>;
  subtotal: number;
  discount: number;
  /** Coupon name printed beside the discount line. */
  discountLabel?: string | null;
  tax: number;
  total: number;
  payments: Array<{ method: string; amount: number }>;
  /** Wristband/gate barcode printed at the bottom, when relevant. */
  barcode?: string;
  /** The tax authority's QR (e-invoice accepted for this sale): printed natively under the receipt. */
  einvoiceQr?: string;
  /** How the receipt looks (language, logo, optional lines): branch layout + this terminal's override. */
  layout?: ReceiptLayout;
}

/** Contract implemented by the Electron preload bridge. Absent in a plain browser. */
export interface HardwareBridge {
  printReceipt(data: ReceiptPrintData): Promise<void>;
  openCashDrawer(): Promise<void>;
  onBarcode(cb: (code: string) => void): () => void;
  /** Silent printing of a full-size document (HTML page) on the configured document printer. */
  printDocument?(html: string): Promise<void>;
  /** The hardware screen's channel to the Electron main process (absent in older terminals and in a browser). */
  settings?: HardwareSettingsApi;
}

export type PrinterConnection =
  | { type: "none" }
  | { type: "network"; host: string; port: number }
  | { type: "usb"; vendorId: number; productId: number }
  | { type: "spooler"; printerName: string }
  | { type: "serial"; path: string; baudRate: number };

export interface PrinterSettings {
  connection: PrinterConnection; paperWidth: 58 | 80; cut: "partial" | "full" | "none"; drawerPin: 2 | 5; openDrawerOnCashSale: boolean; copies: number;
  darkness: number; feedLines: number; bandRows: number;
}
export interface DocumentPrinterSettings { deviceName: string | null; paper: "A4" | "A5" | "Letter"; color: boolean }
export interface HardwareConfig { printer: PrinterSettings; scanner: { serial: { enabled: boolean; path: string; baudRate: number } }; documents: DocumentPrinterSettings; terminalId: string }
export interface Discovery {
  platform: string;
  system: Array<{ name: string; isDefault: boolean; status: number; description: string; looksBluetooth: boolean }>;
  usb: Array<{ vendorId: number; productId: number; label: string }>;
  serial: Array<{ path: string; label: string; bluetooth: boolean }>;
}
export interface PrinterStatus { state: "idle" | "printing" | "ok" | "error"; lastError: string | null; lastErrorAt: string | null; lastOkAt: string | null; queued: number; failed: number }
export interface FailedReceipt { id: string; at: string; error: string; receiptNo: string; total: number }
export interface ConnectionTest { ok: boolean; ms: number; message: string; code?: string }

export interface HardwareSettingsApi {
  get(): Promise<HardwareConfig>;
  save(update: { printer?: Partial<PrinterSettings>; documents?: Partial<DocumentPrinterSettings>; scanner?: HardwareConfig["scanner"] }): Promise<HardwareConfig>;
  discover(): Promise<Discovery>;
  testConnection(candidate?: PrinterConnection): Promise<ConnectionTest>;
  printSample(kind: "test-ar" | "test-en" | "diagnostic" | "drawer"): Promise<void>;
  status(): Promise<PrinterStatus>;
  failedJobs(): Promise<FailedReceipt[]>;
  reprint(id: string): Promise<void>;
  dismissJob(id: string): Promise<void>;
  openBluetoothSettings(): Promise<void>;
  appInfo(): Promise<{ version: string; platform: string; arch: string; electron: string; terminalId: string }>;
  onStatus(cb: (s: PrinterStatus) => void): () => void;
  /** Self-update (newer terminals only): download the installer from the signed link, verify size and SHA-256, run it silently. */
  installUpdate?(payload: { version: string; url: string; sha256: string; size: number }): Promise<unknown>;
  onUpdateProgress?(cb: (p: { done: number; total: number }) => void): () => void;
}

export const hasNativeHardware = (): boolean => typeof window !== "undefined" && !!window.hardware;

export async function openCashDrawer(): Promise<"native" | "unavailable"> {
  if (!window.hardware) return "unavailable";
  await window.hardware.openCashDrawer();
  return "native";
}

export interface ReceiptIdentity {
  stationName: string;
  headerLines: string[];
  footer: string | null;
}

export function toPrintData(receipt: Receipt, locale: Locale, identity: ReceiptIdentity, barcode?: string, layout?: ReceiptLayout): ReceiptPrintData {
  const t = receipt.transaction;
  return {
    locale,
    stationName: identity.stationName,
    headerLines: identity.headerLines,
    footer: identity.footer,
    receiptNo: t.receiptNo,
    createdAt: t.createdAt,
    terminal: t.terminal,
    items: receipt.items,
    subtotal: t.subtotal,
    discount: t.discount,
    discountLabel: receipt.discountRule ? localName({ name: receipt.discountRule.name, nameAr: receipt.discountRule.nameAr }, locale) : null,
    tax: t.tax,
    total: t.total,
    payments: receipt.payments,
    barcode,
    einvoiceQr: receipt.einvoice && !receipt.einvoice.simulated && receipt.einvoice.qr.length <= 1000 ? receipt.einvoice.qr : undefined,
    layout,
  };
}

import logoMono from "../assets/brand/logo-mono.png?inline";

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

const RECEIPT_TEXT = {
  ar: { receipt: "إيصال", subtotal: "المجموع الفرعي", discount: "الخصم", tax: "الضريبة", total: "الإجمالي", thanks: "شكراً لزيارتكم أسترو بلانيت", cur: "دينار", cash: "نقداً", card: "بطاقة", cliq: "كليك", credit: "ذمم", free_stamp: "طوابع الولاء" },
  en: { receipt: "Receipt", subtotal: "Subtotal", discount: "Discount", tax: "Tax", total: "Total", thanks: "Thank you for visiting AstroPlanet", cur: "JOD", cash: "Cash", card: "Card", cliq: "CliQ", credit: "Credit", free_stamp: "Loyalty stamps" },
} as const;

/** 80mm receipt as HTML in the receipt's language (RTL for Arabic). Used for browser printing. */
export function receiptHtml(d: ReceiptPrintData): string {
  const ar = d.locale === "ar";
  const tr: Record<string, string> = RECEIPT_TEXT[d.locale];
  const m = (n: number) => `<span dir="ltr">${money(n)} ${tr.cur}</span>`;
  const rows = d.items
    .map((i) => `<tr><td>${esc(localName({ name: i.description, nameAr: i.descriptionAr }, d.locale))}${i.qty > 1 ? ` ×${i.qty}` : ""}</td><td class="r">${m(i.lineTotal)}</td></tr>`)
    .join("");
  const pays = d.payments.map((p) => `<tr><td>${tr[p.method] ?? esc(p.method)}</td><td class="r">${m(p.amount)}</td></tr>`).join("");
  return `<!doctype html><html lang="${d.locale}" dir="${ar ? "rtl" : "ltr"}"><head><meta charset="utf-8"><style>
    @page { size: 80mm auto; margin: 4mm }
    body { font-family: "Cairo","Nunito",sans-serif; font-size: 12px; width: 72mm; margin: 0 auto; color: #000 }
    h1 { font-size: 16px; text-align: center; margin: 0 0 2px } .c { text-align: center } .r { text-align: ${ar ? "left" : "right"} }
    table { width: 100%; border-collapse: collapse } td { padding: 2px 0; vertical-align: top } hr { border: 0; border-top: 1px dashed #000; margin: 6px 0 }
    .tot td { font-weight: 800; font-size: 14px }
  </style></head><body>
    <div class="c"><img src="${logoMono}" alt="${esc(d.stationName)}" style="width:46mm;height:auto"></div>
    ${(d.headerLines ?? []).map((l) => `<div class="c" style="font-size:10px">${esc(l)}</div>`).join("")}
    <div class="c">${tr.receipt} <span dir="ltr">${esc(d.receiptNo)}</span><br><span dir="ltr">${esc(formatDateTime(d.createdAt, d.locale))}</span></div><hr>
    <table>${rows}</table><hr>
    <table>
      ${d.discount || d.tax ? `<tr><td>${tr.subtotal}</td><td class="r">${m(d.subtotal)}</td></tr>` : ""}
      ${d.discount ? `<tr><td>${tr.discount}${d.discountLabel ? ` (${esc(d.discountLabel)})` : ""}</td><td class="r">-${m(d.discount)}</td></tr>` : ""}
      ${d.tax ? `<tr><td>${tr.tax}</td><td class="r">${m(d.tax)}</td></tr>` : ""}
      <tr class="tot"><td>${tr.total}</td><td class="r">${m(d.total)}</td></tr>${pays}
    </table><hr><div class="c">${d.footer?.trim() ? esc(d.footer) : tr.thanks}</div>
    ${d.barcode ? `<div class="c" style="font-family:monospace;margin-top:6px" dir="ltr">*${esc(d.barcode)}*</div>` : ""}
  </body></html>`;
}

/** Prints arbitrary HTML through a hidden iframe (no popup needed in Chromium/Electron). */
export function printHtml(html: string): void {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    setTimeout(() => frame.remove(), 2000);
  }, 150);
}

/** The branch's receipt layout with this terminal's override applied (the server resolves the layers). Falls back to the defaults if it cannot be read: a sale must always print. */
export async function fetchReceiptLayout(): Promise<ReceiptLayout> {
  try {
    return (await api.get<{ layout: ReceiptLayout }>("/receipt-layout")).layout;
  } catch {
    return DEFAULT_RECEIPT_LAYOUT;
  }
}

/** The receipt language is a manager setting: "ui" follows the cashier, "ar" / "en" force Arabic (right-to-left) or English (left-to-right). */
export const receiptLocaleOf = (layout: ReceiptLayout, ui: Locale): Locale => resolveReceiptLocale(layout.language, ui);

/** Native ESC/POS printing in Electron (silent: no dialog); the browser print dialog otherwise. */
export async function printReceipt(transactionId: string, uiLocale: Locale, identityFor: (receiptLocale: Locale) => ReceiptIdentity, barcode?: string): Promise<"native" | "browser"> {
  const [receipt, layout] = await Promise.all([api.get<Receipt>(`/transactions/${transactionId}`), fetchReceiptLayout()]);
  const locale = receiptLocaleOf(layout, uiLocale);
  const data = toPrintData(receipt, locale, identityFor(locale), layout.showBarcode ? barcode : undefined, layout);
  if (window.hardware) {
    await window.hardware.printReceipt(data);
    return "native";
  }
  printHtml(receiptHtml(data));
  return "browser";
}

/**
 * Automatic receipt after a sale. Only prints when a native (silent) thermal printer is
 * available: a plain browser would pop a print dialog after every sale and block the cashier.
 * Browser users print explicitly (e.g. the invoice PDF buttons).
 */
export async function autoPrintReceipt(transactionId: string, uiLocale: Locale, identityFor: (receiptLocale: Locale) => ReceiptIdentity, barcode?: string): Promise<void> {
  if (!window.hardware) return;
  await printReceipt(transactionId, uiLocale, identityFor, barcode);
}

/**
 * Barcode scanning. Electron supplies scans through the bridge. In a plain browser,
 * keyboard-wedge scanners type fast and end with Enter: when no text field has focus we
 * capture that burst and emit it as a scan.
 */
export function startBarcodeCapture(): () => void {
  // Keyboard-wedge scanners (the common USB kind) are detected here in both the browser and
  // Electron; in Electron the native bridge adds serial (COM) scanners on top.
  const stopNative = window.hardware ? window.hardware.onBarcode(emitScan) : () => undefined;
  let buf = "";
  let last = 0;
  const onKey = (e: KeyboardEvent) => {
    const el = e.target as HTMLElement | null;
    if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
    const now = Date.now();
    if (now - last > 60) buf = "";
    last = now;
    if (e.key === "Enter") {
      if (buf.length >= 4) emitScan(buf);
      buf = "";
    } else if (e.key.length === 1) buf += e.key;
  };
  window.addEventListener("keydown", onKey);
  return () => {
    window.removeEventListener("keydown", onKey);
    stopNative();
  };
}
